import { isSuperAdminPath } from "../super-admin/super-admin-path.js";

/** Customer workspace home. Matches `HOME_PATH` in App.tsx. */
export const WORKSPACE_HOME_PATH = "/inbox";

export function organizationSwitcherDestinations(input: {
  organizationIds: readonly number[];
  platformAdmin: boolean;
}): Array<{ kind: "organization"; organizationId: number } | { kind: "super-admin" }> {
  const organizations = input.organizationIds.map((organizationId) => ({
    kind: "organization" as const,
    organizationId,
  }));
  if (!input.platformAdmin) return organizations;
  return [...organizations, { kind: "super-admin" }];
}

export function organizationSwitcherMenuVisible(input: {
  organizationCount: number;
  canCreateOrganization: boolean;
  pendingInvitationCount: number;
  platformAdmin: boolean;
}): boolean {
  return (
    input.platformAdmin ||
    input.organizationCount > 1 ||
    input.canCreateOrganization ||
    input.pendingInvitationCount > 0
  );
}

export type SwitcherChoice =
  | { kind: "organization"; organizationId: number }
  | { kind: "super-admin" };

/**
 * Organization choices persist through the existing selected-organization id.
 * Super Admin navigates only. It never writes that id.
 */
export function applySwitcherChoice(input: {
  choice: SwitcherChoice;
  currentPath: string;
}): { persistOrganizationId: number | null; navigateTo: string | null } {
  switch (input.choice.kind) {
    case "organization":
      return {
        persistOrganizationId: input.choice.organizationId,
        navigateTo: isSuperAdminPath(input.currentPath) ? WORKSPACE_HOME_PATH : null,
      };
    case "super-admin":
      return {
        persistOrganizationId: null,
        navigateTo: "/super-admin",
      };
    default: {
      const unreachable: never = input.choice;
      return unreachable;
    }
  }
}

/**
 * Whether a switcher choice changes the workspace context: another
 * organization, or crossing between a workspace and Super Admin. Re-choosing
 * the current destination changes nothing and shows no loader.
 */
export function switcherChoiceChangesWorkspace(input: {
  choice: SwitcherChoice;
  currentPath: string;
  selectedOrganizationId: number | null;
}): boolean {
  const onSuperAdmin = isSuperAdminPath(input.currentPath);
  switch (input.choice.kind) {
    case "organization":
      return onSuperAdmin || input.choice.organizationId !== input.selectedOrganizationId;
    case "super-admin":
      return !onSuperAdmin;
    default: {
      const unreachable: never = input.choice;
      return unreachable;
    }
  }
}

export function selectedOrganizationIdAfterChoice(
  currentId: number | null,
  choice: { persistOrganizationId: number | null },
): number | null {
  return choice.persistOrganizationId ?? currentId;
}
